import { useTranslation } from "react-i18next";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikTextField } from "../../../../../../../../formik/FormikTextField";
import { IOptionItem } from "../../../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "../../../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { ClaimCollateralType } from "../../../../../../../api/ClientFileApi";
import {
  ClaimCollateralFormValues,
  ClaimFormValues,
  ClaimMaxLength,
  NEW_HOLDING_OPTION_ID,
} from "../../../../ClaimFormFields.types";
import {
  FreeTextCollateralType,
  HoldingCollateralType,
  createEmptyCollateralHolding,
  isHoldingCollateralType,
} from "../../Collaterals.utils";
import { NewHoldingFields } from "../new-holding-fields/NewHoldingFields";

const HALF = { xs: 12, md: 6 };
const FULL = 12;

interface Props {
  formik: FormikProps<ClaimFormValues>;
  index: number;
  typeOptions: IOptionItem[];
  holdingOptionsFor: (type: HoldingCollateralType, collateralKey: string) => IOptionItem[];
  onDelete: () => void;
}

/** One security of the claim: its type, and the asset or the free-text details the type asks for. */
export function CollateralBlock({ formik, index, typeOptions, holdingOptionsFor, onDelete }: Props) {
  const { t } = useTranslation();

  const collateral = formik.values.collaterals[index];
  if (!collateral) {
    return null;
  }

  const path = (field: keyof ClaimCollateralFormValues) => `collaterals.${index}.${field}` as const;
  const fieldId = (field: keyof ClaimCollateralFormValues) => `claim-collateral-${collateral.key}-${field}`;
  const titleId = `claim-collateral-${collateral.key}-title`;
  const title = collateral.type
    ? t(`claims.collaterals.types.${collateral.type}`)
    : t("claims.collaterals.blockTitleUnset");

  const resetTypeSpecificFields = () => {
    void formik.setFieldValue(path("holdingSelection"), null);
    void formik.setFieldValue(path("newHolding"), createEmptyCollateralHolding());
    void formik.setFieldValue(path("details"), null);
  };

  const type = collateral.type;
  const holdingType: HoldingCollateralType | null = isHoldingCollateralType(type) ? type : null;
  const freeTextType: FreeTextCollateralType | null = type !== null && !isHoldingCollateralType(type) ? type : null;

  return (
    <Paper variant="outlined" component="article" aria-labelledby={titleId}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", bgcolor: "action.hover", px: 2, py: 1 }}
      >
        <Typography id={titleId} variant="subtitle2" component="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton aria-label={`${t("claims.collaterals.remove")}: ${title}`} onClick={onDelete}>
          <DeleteOutlineIcon />
        </IconButton>
      </Stack>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={HALF}>
            <FormikAutocompleteSelect
              id={fieldId("type")}
              formik={formik}
              path={path("type")}
              label={t("claims.collaterals.type")}
              options={typeOptions}
              required
              onValueChange={resetTypeSpecificFields}
            />
          </Grid>
          {holdingType && (
            <Grid size={HALF}>
              <FormikAutocompleteSelect
                id={fieldId("holdingSelection")}
                formik={formik}
                path={path("holdingSelection")}
                label={t(`claims.collaterals.holdingQuestion.${holdingType}`)}
                options={holdingOptionsFor(holdingType, collateral.key)}
                required
              />
            </Grid>
          )}
          {freeTextType && (
            <Grid size={FULL}>
              <FormikTextField
                id={fieldId("details")}
                formik={formik}
                path={path("details")}
                label={t(`claims.collaterals.details.${freeTextType}`)}
                multiline={freeTextType !== ClaimCollateralType.WagePledge}
                minRows={freeTextType === ClaimCollateralType.WagePledge ? undefined : 2}
                slotProps={{ input: { inputProps: { maxLength: ClaimMaxLength.collateralDetails } } }}
              />
            </Grid>
          )}
          {holdingType && collateral.holdingSelection === NEW_HOLDING_OPTION_ID && (
            <Grid size={FULL}>
              <NewHoldingFields formik={formik} index={index} type={holdingType} collateralKey={collateral.key} />
            </Grid>
          )}
        </Grid>
      </Box>
    </Paper>
  );
}
